import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import useAuth from '../hooks/useAuth';

// --- ASSETS E ÍCONES ---
import myLogo from '../assets/logonav.png';
import backgroundVideo from '../assets/seu-video.mp4';
import initImage from '../assets/inicialsistema.jpg';
import initImage2 from '../assets/inicialsistema2.jpg';
import initImage3 from '../assets/inicialsistema3.jpg';
import {
    EmailOutlined, LockOutlined, Visibility, VisibilityOff,
    PersonOutline, BusinessOutlined, PhoneOutlined, ArrowForward
} from '@mui/icons-material';

// --- COMPONENTES MATERIAL-UI ---
import {
    Box, Typography, TextField, Button, CssBaseline, Alert,
    CircularProgress, IconButton, InputAdornment, Link, Stack,
    createTheme, ThemeProvider, Paper, Divider, GlobalStyles
} from '@mui/material';

// ============================================================================
// TEMA — alinhado com o resto do app (dark, primary #315e9a, secondary #90caf9)
// ============================================================================
const sophisticatedTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#315e9a',
            light: '#5b8bd9',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#90caf9',
        },
        background: {
            default: '#0f1218',
            paper: '#15181f',
        },
        text: {
            primary: '#f5f5f5',
            secondary: '#a9a9b2',
        },
        divider: 'rgba(255,255,255,0.08)',
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontWeight: 800, letterSpacing: '-0.5px' },
        h5: { fontWeight: 700, letterSpacing: '-0.2px' },
        h6: { fontWeight: 500, lineHeight: 1.5, color: '#e0e0e0' },
        body1: { lineHeight: 1.6 },
        body2: { color: '#b3b3b3' },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            defaultProps: { disableElevation: true },
        },
    },
});

const showcaseItems = [
    { src: initImage,  title: "Dashboards Intuitivos em Tempo Real",   description: "Visualize KPIs, monitore métricas e tome decisões cruciais com dados precisos e atualizados instantaneamente." },
    { src: initImage2, title: "Gestão de Qualidade e Processos",        description: "Mapeie fluxos, identifique gargalos e implemente melhorias contínuas com ferramentas standard de mercado." },
    { src: initImage3, title: "Estratégia e Performance de Pessoas",    description: "Alinhe suas equipes aos objetivos estratégicos da empresa, acompanhando o desenvolvimento e a produtividade." },
];

// ============================================================================
// ESTILO DOS CARDS
// ============================================================================
const cardStyles = {
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: '20px',
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(21, 24, 31, 0.85)',
    backdropFilter: 'blur(20px)',
};

const formVariants = {
    hidden:  { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0,  transition: { duration: 0.4 } },
    exit:    { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const LoginPage = () => {
    const [formMode, setFormMode] = useState('login'); // login | request | forgot

    // Estados dos formulários
    const [email,        setEmail]        = useState('');
    const [password,     setPassword]     = useState('');
    const [fullName,     setFullName]     = useState('');
    const [companyName,  setCompanyName]  = useState('');
    const [phone,        setPhone]        = useState('');
    const [requestEmail, setRequestEmail] = useState('');
    const [forgotEmail,  setForgotEmail]  = useState('');

    // Estados de UI
    const [showPassword,    setShowPassword]    = useState(false);
    const [error,           setError]           = useState('');
    const [successMessage,  setSuccessMessage]  = useState('');
    const [loading,         setLoading]         = useState(false);
    const [showcaseIndex,   setShowcaseIndex]   = useState(0);

    const navigate  = useNavigate();
    const { login } = useAuth();

    useEffect(() => {
        const intervalId = setInterval(() => {
            setShowcaseIndex(prev => (prev + 1) % showcaseItems.length);
        }, 7000);
        return () => clearInterval(intervalId);
    }, []);

    const handleFormSwitch = (mode) => {
        setFormMode(mode);
        setError('');
        setSuccessMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setLoading(true);

        if (formMode === 'login') {
            try {
                const response = await api.post('/auth/login', { email, password });
                const { token } = response.data;
                login(token);
                navigate('/inicial');
            } catch (err) {
                setError(err.response?.data?.message || 'Credenciais inválidas.');
            } finally {
                setLoading(false);
            }

        } else if (formMode === 'request') {
            try {
                await api.post('/access-requests', {
                    fullName,
                    email:       requestEmail,
                    companyName,
                    phone,
                });
                setSuccessMessage('Solicitação enviada com sucesso! Entraremos em contato em breve.');
                setFullName('');
                setRequestEmail('');
                setCompanyName('');
                setPhone('');
            } catch (err) {
                setError(err.response?.data?.message || 'Falha ao enviar solicitação.');
            } finally {
                setLoading(false);
            }

        } else if (formMode === 'forgot') {
            try {
                await api.post('/auth/forgot-password', { email: forgotEmail });
                setSuccessMessage('Se uma conta existir, um link de recuperação foi enviado para seu email.');
                setForgotEmail('');
            } catch (err) {
                setError(err.response?.data?.message || 'Falha ao enviar o pedido.');
            } finally {
                setLoading(false);
            }
        }
    };

    const currentShowcaseItem = showcaseItems[showcaseIndex];

    return (
        <ThemeProvider theme={sophisticatedTheme}>
            <GlobalStyles styles={{
                'html, body, #root': {
                    backgroundColor: '#0f1218',
                    margin: 0,
                    padding: 0,
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden',
                },
            }} />
            <CssBaseline />
            <Box sx={{ position: 'relative', height: '100vh', width: '100vw', overflow: 'hidden' }}>

                {/* Vídeo de fundo */}
                <Box
                    component="video"
                    autoPlay loop muted
                    src={backgroundVideo}
                    sx={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }}
                />

                {/* Overlay escuro */}
                <Box sx={{
                    position: 'absolute', width: '100%', height: '100%',
                    background: 'linear-gradient(75deg, rgba(15, 18, 24, 0.92) 0%, rgba(15, 18, 24, 0.75) 100%)',
                    zIndex: 2,
                }} />

                {/* Conteúdo */}
                <Box sx={{
                    position: 'relative', zIndex: 3, height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: { lg: 6, xl: 8 }, p: 4, transform: 'scale(0.95)',
                }}>

                    {/* === CARD ESQUERDO: FORMULÁRIOS === */}
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        elevation={0}
                        sx={{ ...cardStyles, width: '100%', maxWidth: '440px', p: { xs: 3, sm: 5 }, minHeight: '540px' }}
                    >
                        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>

                            {/* LOGO RETANGULAR */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                mb: 1,
                            }}>
                                <Box
                                    component="img"
                                    src={myLogo}
                                    alt="Logo"
                                    sx={{
                                        width: '180px',
                                        maxWidth: '70%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        display: 'block',
                                    }}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography component="h1" variant="h5" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                                    {formMode === 'login'   && 'Acesso à Plataforma'}
                                    {formMode === 'request' && 'Solicitar Acesso'}
                                    {formMode === 'forgot'  && 'Recuperar Senha'}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {formMode === 'login'   && 'Gerencie sua empresa com excelência.'}
                                    {formMode === 'request' && 'Preencha os dados para uma demonstração.'}
                                    {formMode === 'forgot'  && 'Insira seu email para enviarmos as instruções.'}
                                </Typography>
                            </Box>

                            <Divider sx={{ width: '80%', my: 2, borderColor: 'rgba(255,255,255,0.06)' }} />

                            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', minHeight: '250px' }}>
                                <AnimatePresence mode="wait">

                                    {formMode === 'login' && (
                                        <motion.div key="login" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                            <Stack spacing={2.5}>
                                                <TextField
                                                    required fullWidth
                                                    label="Seu Email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <EmailOutlined sx={{ color: 'secondary.main' }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    required fullWidth
                                                    label="Sua Senha"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LockOutlined sx={{ color: 'secondary.main' }} />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                    sx={{ color: 'text.secondary' }}
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Stack>
                                        </motion.div>
                                    )}

                                    {formMode === 'request' && (
                                        <motion.div key="request" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                            <Stack spacing={2}>
                                                <TextField
                                                    required fullWidth label="Nome Completo"
                                                    value={fullName} onChange={(e) => setFullName(e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                                                />
                                                <TextField
                                                    required fullWidth label="Email Corporativo" type="email"
                                                    value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                                                />
                                                <TextField
                                                    required fullWidth label="Nome da Empresa"
                                                    value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><BusinessOutlined sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                                                />
                                                <TextField
                                                    fullWidth label="Telefone (Opcional)"
                                                    value={phone} onChange={(e) => setPhone(e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                                                />
                                            </Stack>
                                        </motion.div>
                                    )}

                                    {formMode === 'forgot' && (
                                        <motion.div key="forgot" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                            <Stack spacing={2.5}>
                                                <TextField
                                                    required fullWidth label="Email de Recuperação" type="email"
                                                    value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                                                />
                                            </Stack>
                                        </motion.div>
                                    )}

                                </AnimatePresence>

                                {error          && <Alert severity="error"   sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
                                {successMessage && <Alert severity="success" sx={{ mt: 2, width: '100%' }}>{successMessage}</Alert>}

                                <Button
                                    type="submit" fullWidth variant="contained"
                                    disabled={loading} size="large"
                                    endIcon={!loading && <ArrowForward />}
                                    sx={{
                                        mt: 3,
                                        py: 1.4,
                                        borderRadius: 2,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    {loading ? <CircularProgress size={26} color="inherit" /> :
                                        (formMode === 'login'   && 'Entrar')              ||
                                        (formMode === 'request' && 'Enviar Solicitação')  ||
                                        (formMode === 'forgot'  && 'Enviar Link')}
                                </Button>
                            </Box>

                            <Stack spacing={1} alignItems="center">
                                {formMode === 'login' && (
                                    <Link
                                        component="button" type="button"
                                        onClick={() => handleFormSwitch('forgot')}
                                        variant="body2"
                                        sx={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'secondary.main',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        Esqueceu sua senha?
                                    </Link>
                                )}
                                {(formMode === 'request' || formMode === 'forgot') && (
                                    <Link
                                        component="button" type="button"
                                        onClick={() => handleFormSwitch('login')}
                                        variant="body2"
                                        sx={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'secondary.main',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        Voltar para o Login
                                    </Link>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* === CARD DIREITO: SHOWCASE === */}
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        elevation={0}
                        sx={{
                            ...cardStyles,
                            display: { xs: 'none', md: 'flex' },
                            width: '100%', maxWidth: '700px', minHeight: '600px',
                            p: { md: 4, lg: 5 },
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', width: '100%' }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography component="h2" variant="h4" sx={{ color: 'secondary.main', fontWeight: 800 }}>
                                    Desbloqueie a Alta Performance
                                </Typography>
                                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                    Sua plataforma central para decisões estratégicas.
                                </Typography>
                            </Box>

                            <Box sx={{
                                flexGrow: 1, position: 'relative', minHeight: '300px',
                                borderRadius: '16px', overflow: 'hidden',
                                border: '1px solid rgba(255, 255, 255, 0.06)',
                                backgroundColor: 'rgba(15, 18, 24, 0.6)',
                            }}>
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={showcaseIndex}
                                        src={currentShowcaseItem.src}
                                        alt={currentShowcaseItem.title}
                                        initial={{ opacity: 0, scale: 1.05 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 1.2, ease: 'easeInOut' }}
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        style={{
                                            position: 'absolute',
                                            width: '100%', height: '100%',
                                            objectFit: 'contain',
                                            borderRadius: 'inherit',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </AnimatePresence>
                            </Box>

                            <Box sx={{ mt: 2, minHeight: '80px' }}>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={showcaseIndex}
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -15 }}
                                        transition={{ duration: 0.5, ease: 'circOut' }}
                                    >
                                        <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                                            {currentShowcaseItem.title}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {currentShowcaseItem.description}
                                        </Typography>
                                    </motion.div>
                                </AnimatePresence>
                            </Box>

                            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)', width: '100%' }}>
                                <Button
                                    variant="contained" size="large" fullWidth
                                    endIcon={<ArrowForward />}
                                    onClick={() => handleFormSwitch('request')}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 20px rgba(49, 94, 154, 0.4)',
                                        },
                                    }}
                                >
                                    Solicite um Acesso
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default LoginPage;